import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fetchMe } from '@/api/member';
import { useAuth } from '@/contexts/AuthContext';

const MeCtx = createContext(null);

/**
 * MeProvider holds the full member profile (balances, wallet addresses,
 * referral data) returned by GET /api/me. It listens to AuthContext so that
 * the moment a JWT lands (signup/login) it triggers a fresh /api/me fetch —
 * no full page reload required.
 */
export const MeProvider = ({ children }) => {
  const { authUser, bootLoading } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!authUser) {
      setMe(null);
      setError(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchMe();
      setMe(data);
      setError(null);
    } catch (e) {
      setMe(null);
      setError(e?.response?.data?.detail || e.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  useEffect(() => {
    if (bootLoading) return; // wait for AuthContext to finish its boot probe
    refresh();
  }, [bootLoading, refresh]);

  return <MeCtx.Provider value={{ me, loading: loading || bootLoading, error, refresh, setMe }}>{children}</MeCtx.Provider>;
};

export const useMe = () => {
  const ctx = useContext(MeCtx);
  if (!ctx) throw new Error('useMe must be used inside <MeProvider>');
  return ctx;
};
